// Guided tutorial content. The live copy is site/tutorial.json in storage, edited by admins at
// #/admin/tutorial; this file is the built-in starting point, used until an admin saves a version.
import { MEDIA_BASE } from '@archviewvr/config';

export interface TutorialMedia {
  /** Image, video file (.mp4/.webm) or a YouTube/Vimeo link. Relative paths are files in the dashboard. */
  src: string;
  alt?: string;
}
export interface TutorialStep {
  id: string;
  heading: string;
  /** Short text. Blank lines split paragraphs, lines starting "- " are bullets, **bold** works. */
  body: string;
  media?: TutorialMedia;
  download?: { label: string; url: string };
  hidden?: boolean;
}
export interface TutorialChapter {
  id: string;
  title: string;
  summary: string;
  /** Software or software/renderer ids this chapter is for (e.g. "3dsmax", "3dsmax/corona"). Empty = everyone. */
  for: string[];
  steps: TutorialStep[];
}
export interface TutorialRenderer { id: string; label: string; available: boolean }
export interface TutorialSoftware { id: string; label: string; available: boolean; renderers: TutorialRenderer[] }
export interface TutorialData {
  version: 1;
  welcome: { heading: string; body: string; media?: TutorialMedia };
  finish: { heading: string; body: string; media?: TutorialMedia };
  software: TutorialSoftware[];
  chapters: TutorialChapter[];
}

const MAX_CORONA = ['3dsmax/corona'];
const MAX = ['3dsmax'];

export const DEFAULT_TUTORIAL: TutorialData = {
  version: 1,
  welcome: {
    heading: 'Welcome to ArchviewVR',
    body: 'Turn your 360 renders into a tour people can walk through. This guide takes you from your 3D scene to a published tour, one step at a time.',
    media: { src: '/tutorial/tour.jpg', alt: 'A walkable ArchviewVR tour' },
  },
  finish: {
    heading: 'You’re ready',
    body: 'Start a project and drop your folder in. You can open this guide again any time from your projects page.',
    media: { src: '/tutorial/tour.jpg', alt: 'A walkable ArchviewVR tour' },
  },
  software: [
    { id: '3dsmax', label: '3ds Max', available: true, renderers: [
      { id: 'corona', label: 'Corona', available: true },
      { id: 'vray', label: 'V-Ray', available: false },
    ] },
    { id: 'blender', label: 'Blender', available: false, renderers: [] },
    { id: 'sketchup', label: 'SketchUp', available: false, renderers: [] },
  ],
  chapters: [
    {
      id: 'cameras', title: 'Place your cameras', summary: 'Where to put cameras so walking feels smooth', for: MAX,
      steps: [
        { id: 'spacing', heading: 'A camera every 2–4 metres', media: { src: '/tutorial/plan-cameras.svg', alt: 'Floor plan with cameras spaced through each room' },
          body: 'Put a camera in every room, along hallways and in each doorway. Each camera should be able to see the next one, so the tour can glide between them.\n\nMore cameras make walking smoother. Most homes need 20 to 60.' },
        { id: 'height', heading: 'Eye height, perfectly level', media: { src: '/tutorial/eye-height.svg', alt: 'Camera 1.6 metres above the floor, facing straight ahead' },
          body: 'Set every camera about 1.6 m above its floor and keep it level: no tilt and no roll.\n\nOn upper floors, measure from that floor.' },
        { id: 'names', heading: 'Name cameras clearly', media: { src: '/tutorial/names.svg', alt: 'Camera names matching render file names' },
          body: 'Each render is matched to its camera by name, so give cameras short names without spaces, like **Cam_Kitchen_01**.' },
      ],
    },
    {
      id: 'corona', title: 'Set up Corona', summary: '360° cameras and a consistent look', for: MAX_CORONA,
      steps: [
        { id: 'spherical', heading: 'Switch the cameras to 360°', media: { src: '/tutorial/corona-camera.svg', alt: 'Corona camera settings for 360 renders' },
          body: 'Use Corona cameras. In each camera’s settings, set the projection to **Spherical (360°)**.\n\nSet the output size to exactly 2:1, such as 8000 × 4000. Bigger looks sharper when people stop to look around.' },
        { id: 'exposure', heading: 'Keep the look the same everywhere', media: { src: '/tutorial/exposure.svg', alt: 'Matching exposure blends smoothly, mismatched exposure flickers' },
          body: 'ArchviewVR blends neighbouring renders as people walk, so they need to match.\n\n- Turn off any auto exposure\n- Use the same exposure and white balance on every camera\n- Keep the same LightMix and tone mapping' },
      ],
    },
    {
      id: 'render', title: 'Render', summary: 'Batch render every camera overnight', for: MAX_CORONA,
      steps: [
        { id: 'batch', heading: 'Batch render every camera', media: { src: '/tutorial/spherical.svg', alt: 'A 2:1 spherical render' },
          body: 'Open **Rendering › Batch Render** and add a view for each camera. Save each one as a JPG (quality 90–95), named exactly like its camera.\n\nLet it run overnight.' },
      ],
    },
    {
      id: 'export', title: 'Export points and model', summary: 'The camera points file and a simple model', for: MAX,
      steps: [
        { id: 'script', heading: 'Run the ArchviewVR script', media: { src: '/tutorial/script.svg', alt: 'The camera script saving points.txt' },
          download: { label: 'Download the ArchviewVR Toolkit', url: '/tools/archviewvr-toolkit.ms' },
          body: 'In 3ds Max, choose **Scripting › Run Script** and pick the Toolkit. It walks you through it:\n\n1. Choose a project folder.\n2. **Cameras**: click floors to place them, or fill selected floors, then **Check**.\n3. **Model**: make a proxy and **Export model.obj**.\n4. **Render**: **Set up renders**, then **Render all**.\n5. **Export points** and **Check folder**, then drop the folder into ArchviewVR.' },
        { id: 'proxy', heading: 'Export a simple proxy model', media: { src: '/tutorial/proxy.svg', alt: 'A simple model of floors and walls' },
          body: 'Make a light version of the building: floors, walls, ceilings, stairs and big joinery. ArchviewVR paints your renders onto it.\n\nCap the windows and keep it under 150K polygons. Don’t move it. In the exporter, select it and press **Export model** (it shows the polygon count as you select). Or use **File › Export › Export Selected** as OBJ, in the same units as the scene.' },
      ],
    },
    {
      id: 'folder', title: 'Organise your folder', summary: 'Everything in one place, ready to drop', for: [],
      steps: [
        { id: 'folder', heading: 'One folder, ready to drop', media: { src: '/tutorial/folder.svg', alt: 'A folder with the model, points file and renders' },
          body: 'Put everything in one folder: the model, **points.txt** and all the renders. Subfolders are fine.\n\nYou’ll drop the whole folder into ArchviewVR in one go.' },
      ],
    },
    {
      id: 'upload', title: 'Upload to ArchviewVR', summary: 'Drop the folder and check the result', for: [],
      steps: [
        { id: 'drop', heading: 'Start a project and drop the folder', media: { src: '/tutorial/upload-drop.jpg', alt: 'The upload page' },
          body: 'Click **New project**, give it a name, then drop your folder onto the page.' },
        { id: 'read', heading: 'Watch it come together', media: { src: '/tutorial/upload-read.jpg', alt: 'Renders being read with ticks' },
          body: 'Everything is processed right here in your browser: the model, the camera points, levels and every render. Ticks show what’s ready.\n\nNothing uploads until you’ve checked it.' },
        { id: 'check', heading: 'Check anything flagged', media: { src: '/tutorial/check.jpg', alt: 'The check step' },
          body: 'ArchviewVR lines each render up with the model. If one looks off it’s flagged. Compare the outlines, use the suggested fix or slide to adjust, then save.' },
        { id: 'update', heading: 'Changes later? Drop just those files', media: { src: '/tutorial/upload-drop.jpg', alt: 'The upload page' },
          body: 'Re-rendered a room? Drop only the new renders. Ones with the same name replace the old ones, new names are added, and a new model or points file replaces the old one. Hotspots, room names and settings stay as they are.' },
      ],
    },
    {
      id: 'setup', title: 'Make it shine', summary: 'Start view, room names and publishing', for: [],
      steps: [
        { id: 'setup', heading: 'Set up the tour', media: { src: '/tutorial/setup.jpg', alt: 'The tour setup screen' },
          body: 'Choose where people start, name the rooms and pick how people can move around. The preview updates as you go.' },
        { id: 'publish', heading: 'Publish and share', media: { src: '/tutorial/publish.jpg', alt: 'A published project with its share link' },
          body: 'Publish to get a link you can send to buyers or embed on a website. Cancel any time.' },
      ],
    },
  ],
};

const LIVE_URL = () => `${MEDIA_BASE}/site/tutorial.json`;
let cached: TutorialData | null = null;

/** The tutorial as last saved by an admin, or the built-in one. */
export async function loadTutorial(fresh = false): Promise<TutorialData> {
  if (cached && !fresh) return cached;
  try {
    const res = await fetch(`${LIVE_URL()}?t=${Date.now()}`, { cache: 'no-store' });
    if (res.ok) {
      const data = (await res.json()) as TutorialData;
      if (data?.version === 1 && Array.isArray(data.chapters)) return (cached = data);
    }
  } catch { /* not saved yet, or offline */ }
  return (cached = structuredClone(DEFAULT_TUTORIAL));
}
export const forgetTutorial = () => { cached = null; };

/** Absolute URL for a media path (dashboard files stay relative to this site). */
export const mediaUrl = (src: string) => src;

export type MediaKind = 'image' | 'video' | 'youtube' | 'vimeo';
export function mediaKind(src: string): MediaKind {
  if (/youtu\.?be/.test(src)) return 'youtube';
  if (/vimeo\.com/.test(src)) return 'vimeo';
  if (/\.(mp4|webm)(\?|$)/i.test(src)) return 'video';
  return 'image';
}

/** Does a chapter apply to this software and renderer? */
export const chapterFor = (c: TutorialChapter, software: string, renderer: string | null) =>
  !c.for.length || c.for.includes(software) || (renderer != null && c.for.includes(`${software}/${renderer}`));
